/* eslint-disable @typescript-eslint/no-explicit-any */

import { useLazyQuery, useMutation } from "@apollo/client/react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FilterOptionsState,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FloppyDiskBackIcon } from "@phosphor-icons/react/dist/csr/FloppyDiskBack";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { FC, useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import Permissions from "../../constants/permissions";
import { GET_INTEGRATION_CHANNEL_QUERY, SAVE_INTEGRATION_MUTATION } from "../../graphql";
import { Integration } from "../../graphql/types";
import { getObjectField, NestedObject } from "../../lib/Util";
import { IfPermission } from "../Permissions";
import { GqlError } from "../common/GqlError";
import { IconButton } from "../ui/icons";
import { StatefulButton } from "../ui/muiButtons";
import { BaseIntegration } from "./BaseIntegration";

class SlackIntegration extends BaseIntegration {
  displayName() {
    return getObjectField(this as unknown as NestedObject, "public_authorization.team_name") || "Pending OAuth";
  }

  sortName() {
    return getObjectField(this as unknown as NestedObject, "public_authorization.team_name") || " Pending OAuth";
  }

  errors() {
    const errorMessages: any = [];

    const authError = getObjectField(this as unknown as NestedObject, "public_authorization.error");
    if (authError) {
      errorMessages.push({ message: authError });
    }

    const filters = getObjectField(this as unknown as NestedObject, "filters") || [];
    if (Array.isArray(filters)) {
      filters.forEach((filter, index) => {
        const filterError = getObjectField(filter, "configuration.error");
        if (filterError) {
          errorMessages.push({
            message: filterError,
            path: `filters[${index}].configuration`,
          });
        }
      });
    }

    return errorMessages;
  }
}

type AutocompleteOptions = {
  id: string;
  name: string;
  is_member: boolean;
  is_general: boolean;
  purpose: string;
};

interface ISlackIntegrationEditor {
  integration: Integration;
}

type Configuration = {
  id?: any;
  name?: any;
  is_member?: any;
  channel_id?: any;
  channel_name?: any;
  is_general?: any;
  error?: any;
};

type ParsedFilterConfiguration = Pick<
  Configuration,
  "channel_id" | "channel_name" | "is_general" | "is_member" | "error"
> | null;

const SlackIntegrationEditor: FC<ISlackIntegrationEditor> = ({ integration }) => {
  const onCompleted = () => {
    toast.success("The integration settings have been saved.");
  };

  const onError = () => {
    toast.error("The integration settings were not saved, because there are errors to fix.");
  };
  const [saveIntegration, { loading, error }] = useMutation(SAVE_INTEGRATION_MUTATION, {
    refetchQueries: ["list_integrations"],
    onCompleted: onCompleted,
    onError: onError,
  });
  const onSubmit = (data: {
    authorization: any;
    configuration: any;
    filters: { configuration: Configuration | null }[];
  }) => {
    const { authorization, configuration, filters } = data;

    filters.forEach((filter) => {
      filter.configuration = parseOption(filter.configuration);
    });

    const payload = {
      variables: {
        id: integration.id,
        integration_type: integration.integration_type,
        authorization: authorization,
        configuration: configuration,
        filters: filters,
      },
    };
    saveIntegration(payload);
  };

  const channels = useMemo(
    () => getObjectField(integration as unknown as NestedObject, "assistance.channels") || [],
    [integration]
  );
  // True when the channel list couldn't be fully loaded (too many channels to page through, or the fetch failed) —
  // see resolve_assistance/get_assistance on the backend.
  const channelsTruncated = getObjectField(integration as unknown as NestedObject, "assistance.truncated") === true;

  // Given the channel chosen in the typeahead or dropdown, return the new filter configuration.
  const parseOption = (selected: Configuration | null): ParsedFilterConfiguration => {
    if (!selected) {
      return null;
    }
    return {
      channel_id: selected.id,
      channel_name: selected.name,
      is_general: selected.is_general,
      is_member: selected.is_member,
      error: null,
    };
  };

  // TODO: Fix this, the types defined in this function doesn't match with the ones defined in the component (21025)
  // Given the the channel from assistance, render it's dropdown item.
  const renderOption = (option: any, props: any, index: any) => {
    return (
      <li {...option} key={index}>
        <Stack sx={{ flexDirection: "column" }}>
          <p>
            <b>{props.name}</b>
          </p>
          <p>{props.purpose}</p>
        </Stack>
      </li>
    );
  };

  const { control, handleSubmit, formState, setValue, getValues } = useForm<any>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {},
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
  });

  useEffect(() => {
    // Given the current filter configuration, find the channel from the assistance.
    const formatOption = (configuration?: Configuration) => {
      if (!configuration || !configuration.channel_id) {
        return [];
      }
      const selected = Array.isArray(channels) && channels.find((c) => c.id === configuration.channel_id);
      if (selected) {
        return [selected];
      }
      // If the channel was removed from the Slack workspace, we won't find it in the list from assistance, so
      // we must recreate it.
      return [
        {
          id: configuration.channel_id,
          name: configuration.channel_name,
          is_general: configuration.is_general,
          is_member: configuration.is_member,
        },
      ];
    };

    append("filters");
    if (integration.filters.length > 0) {
      integration.filters?.forEach((filter, idx) => {
        const channel = formatOption(filter?.configuration);
        setValue(`filters[${idx}].configuration`, channel[0]);
        setValue(`filters[${idx}].production`, filter?.production);
        setValue(`filters[${idx}].proofs`, filter?.proofs);
        setValue(`filters[${idx}].lateUploads`, filter?.lateUploads);
        setValue(`filters[${idx}].backfills`, filter?.backfills);
        setValue(`filters[${idx}].beta`, filter?.beta);
      });
    }
  }, [append, channels, integration, setValue]);

  const validateButtonRules = (index: number): string | undefined => {
    if (
      !getValues(`filters[${index}].production`) &&
      !getValues(`filters[${index}].backfills`) &&
      !getValues(`filters[${index}].lateUploads`) &&
      !getValues(`filters[${index}].proofs`) &&
      !getValues(`filters[${index}].beta`)
    )
      return "At least one filter must be selected";
  };

  const checkError = (index: number) => {
    if (formState.errors.filters) {
      return Boolean((formState.errors.filters as any)[index]?.production);
    }
    return false;
  };

  const PURPOSE_TRUNCATE_LENGTH = 90;
  const truncate = (text: string, maxLength: number) =>
    text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}...` : text;

  const filterOptionsAutocomplete = (options: Array<AutocompleteOptions>, state: FilterOptionsState<any>) => {
    const newOptions: Array<AutocompleteOptions> = [];
    options.forEach((element) => {
      if (
        element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
        element.purpose.toLowerCase().includes(state.inputValue.toLowerCase())
      ) {
        newOptions.push(element);
      }
    });
    return newOptions;
  };

  // For accounts with too many channels to list (e.g. via the typeahead), allow selecting a channel directly by id.
  const [channelIdInputs, setChannelIdInputs] = useState<Record<number, string>>({});
  // The dropdown can only reliably display a value that's part of its (statically loaded) options, so once a
  // channel is set via lookup, blank the dropdown out rather than have it show a stale/incorrect selection.
  const [selectedViaLookup, setSelectedViaLookup] = useState<Record<number, boolean>>({});
  const [lookupChannel, { loading: channelLookupLoading }] = useLazyQuery(GET_INTEGRATION_CHANNEL_QUERY, {
    fetchPolicy: "network-only",
  });
  const watchedFilters = useWatch({ control, name: "filters" }) as
    Array<{ configuration?: AutocompleteOptions | null }> | undefined;

  const lookupChannelById = async (index: number) => {
    const channelId = (channelIdInputs[index] || "").trim();
    if (!channelId) {
      return;
    }
    const { data, error: lookupError } = await lookupChannel({ variables: { id: integration.id, channelId } });
    const channel = (data as any)?.integration?.channel_by_id;
    if (lookupError || !channel || channel.error) {
      toast.error(channel?.error || `Could not find a channel with id "${channelId}".`);
      return;
    }
    setValue(`filters[${index}].configuration`, channel, { shouldValidate: true });
    setSelectedViaLookup((prev) => ({ ...prev, [index]: true }));
    toast.success(`Found channel "#${channel.name}"${channel.purpose ? ` — ${channel.purpose}` : ""}`);
  };

  const clearChannelSelection = (index: number) => {
    setValue(`filters[${index}].configuration`, null, { shouldValidate: true });
    setChannelIdInputs((prev) => ({ ...prev, [index]: "" }));
    setSelectedViaLookup((prev) => ({ ...prev, [index]: false }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {error ? <GqlError error={error} /> : null}
      {/* this should be checked with further info. ArrayItems returns any[] which is not allowed by ts */
      /* @ts-ignore */}
      {fields.map((field, index) => (
        <Grid key={field.id} container sx={{ flexDirection: "column", mt: 3 }}>
          <Grid container sx={{ flexDirection: "row" }}>
            <Grid size={2}></Grid>
            <Grid size={8}>
              <Controller
                control={control}
                name={`filters.id`}
                defaultValue={integration.id}
                render={({ field }) => {
                  return <input {...field} type="hidden" />;
                }}
              />
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
                <Controller
                  control={control}
                  name={`filters[${index}].configuration`}
                  rules={{ required: true }}
                  render={({ field: { onChange, value, ...rest } }) => {
                    return (
                      <Autocomplete
                        // Remount when toggling to/from a lookup-based selection, since Autocomplete doesn't reliably
                        // reset its internal displayed text just because the `value` prop changes externally.
                        key={selectedViaLookup[index] ? "lookup" : "dropdown"}
                        disablePortal
                        id="Channel"
                        data-cy="Channel"
                        value={selectedViaLookup[index] ? null : value}
                        options={Array.isArray(channels) ? channels : []}
                        filterOptions={filterOptionsAutocomplete}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => {
                          const hasConfigurationError = Boolean(
                            formState.errors.filters && (formState.errors.filters as any)[index]?.configuration
                          );
                          return (
                            <TextField
                              {...params}
                              label="Channel"
                              error={hasConfigurationError}
                              helperText={
                                (hasConfigurationError &&
                                  "A channel is required — select one above or look it up by ID below.") ||
                                (channelsTruncated &&
                                  "This workspace has too many channels to load automatically. Please look it up by ID below.") ||
                                "This typeahead was statically populated when the form was loaded."
                              }
                              slotProps={{
                                ...params.slotProps,
                                formHelperText:
                                  channelsTruncated && !hasConfigurationError
                                    ? { sx: { color: "warning.main" } }
                                    : undefined,
                              }}
                            />
                          );
                        }}
                        onChange={(e, data) => {
                          setSelectedViaLookup((prev) => ({ ...prev, [index]: false }));
                          setChannelIdInputs((prev) => ({ ...prev, [index]: "" }));
                          onChange(data);
                        }}
                        renderOption={renderOption}
                        {...rest}
                      />
                    );
                  }}
                />
                <Divider sx={{ my: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    OR
                  </Typography>
                </Divider>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Enter channel ID"
                    placeholder="e.g. C0123456789"
                    value={channelIdInputs[index] || ""}
                    onChange={(e) => setChannelIdInputs((prev) => ({ ...prev, [index]: e.target.value }))}
                  />
                  <Button
                    size="small"
                    disabled={channelLookupLoading}
                    startIcon={channelLookupLoading ? <CircularProgress size={14} /> : null}
                    onClick={() => lookupChannelById(index)}
                  >
                    {channelLookupLoading ? "Looking up..." : "Look up"}
                  </Button>
                </Stack>
                {watchedFilters?.[index]?.configuration?.id ? (
                  <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", mt: 1.5 }}>
                    <Typography variant="body2">
                      <b>#{watchedFilters[index]?.configuration?.name}</b>
                      {watchedFilters[index]?.configuration?.purpose ? (
                        <Tooltip title={watchedFilters[index]?.configuration?.purpose}>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {" "}
                            — {truncate(watchedFilters[index]?.configuration?.purpose, PURPOSE_TRUNCATE_LENGTH)}
                          </Typography>
                        </Tooltip>
                      ) : null}
                    </Typography>
                    <Button size="small" color="error" onClick={() => clearChannelSelection(index)}>
                      Clear
                    </Button>
                  </Stack>
                ) : null}
              </Box>
            </Grid>
            <IfPermission permission={Permissions.ManageIntegrations}>
              <Grid container size={1} sx={{ justifyContent: "flex-end" }}>
                <IconButton onClick={() => remove(index)}>
                  <TrashIcon />
                </IconButton>
              </Grid>
            </IfPermission>
          </Grid>
          <Grid container sx={{ flexDirection: "row" }}>
            <Grid size={2} />
            <Grid container size={10} sx={{ flexDirection: "column" }}>
              <Controller
                control={control}
                name={`filters[${index}].production`}
                rules={{
                  validate: () => validateButtonRules(index),
                }}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      label="Production"
                      control={<Switch id={`production${index}`} checked={field.value} {...field} />}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={`filters[${index}].backfills`}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      label="Backfills"
                      control={<Switch id={`backfills${index}`} checked={field.value} {...field} />}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={`filters[${index}].proofs`}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      label="Tests"
                      control={<Switch id={`proofs${index}`} checked={field.value} {...field} />}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={`filters[${index}].lateUploads`}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      label="Late Uploads"
                      control={<Switch id={`lateUploads${index}`} checked={field.value} {...field} />}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name={`filters[${index}].beta`}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      label="Beta"
                      control={<Switch id={`beta${index}`} checked={field.value} {...field} />}
                    />
                  );
                }}
              />
              {checkError(index) ? (
                <Typography sx={{ color: "accentDanger30.main" }}>At least one option must be selected</Typography>
              ) : null}
            </Grid>
          </Grid>
          <Grid size={{ sm: 1 }} />
        </Grid>
      ))}

      <Grid container sx={{ flexDirection: "row" }}>
        <Grid size={{ sm: 2 }} />
        <Grid size={{ sm: 8 }}>
          <Button name="filters" onClick={() => append("filters")}>
            Add channels
          </Button>
        </Grid>
        <Grid size={{ sm: 2 }} />
      </Grid>
      <Grid container sx={{ justifyContent: "flex-end", flexDirection: "row" }}>
        <IfPermission permission={Permissions.ManageIntegrations}>
          <StatefulButton
            id="saveIntegration"
            type="submit"
            loading={loading}
            startIcon={<FloppyDiskBackIcon width="12px" />}
          >
            Save
          </StatefulButton>
        </IfPermission>
      </Grid>
    </form>
  );
};

const SLACK_INTEGRATION = {
  integrationType: "slack",
  title: "Slack Integration",
  short_title: "Slack",
  description: <p>Receive events, resolve issues and check status from within Slack.</p>,
  image: "/img/integrations/slack.png",
  oauth: true,
  clazz: SlackIntegration,
  editor: SlackIntegrationEditor,
};

const HIDDEN_SLACK_INTEGRATION = {
  integrationType: "hidden-slack",
  title: "Hidden Slack Integration",
  short_title: "Hidden Slack",
  hidden: true,
  description: <p>Receive events, resolve issues and check status from within Slack&mdash;only CS can see this.</p>,
  image: "/img/integrations/slack.png",
  oauth: true,
  clazz: SlackIntegration,
  editor: SlackIntegrationEditor,
};

export { HIDDEN_SLACK_INTEGRATION, SLACK_INTEGRATION };
